/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --padding: 0 var(--nve-ref-size-800) 0 var(--nve-ref-size-200);
  --font-size: var(--nve-ref-font-size-100);
  --height: var(--nve-ref-size-800);
  --background: var(--nve-sys-interaction-field-background);
  --color: var(--nve-sys-interaction-field-color);
  --border-radius: var(--nve-ref-border-radius-sm);
  --border: var(--nve-ref-border-width-md) solid transparent;
  --border-bottom: var(--border);
  --gap: var(--nve-ref-space-xxs);
  --cursor: pointer;
  --font-weight: normal;
  --text-align: start;
  --scroll-height: 50vh;
  --width: 100%;
  --control-width: var(--width);
  --max-width: 100%;
  --min-width: fit-content;
  --_input-background: var(--background);
  --_icon-color: var(--color);
  --_gap: var(--gap);
  --size: 1;
  --_height: var(--height);
  contain: initial;
  width: var(--width);
  max-width: var(--max-width);
  min-width: var(--min-width);
}

:host([fit-text]) {
  --min-width: initial;
}

[internal-host] {
  position: relative;
}

[input] {
  scroll-behavior: smooth;
  scrollbar-color: color-mix(in oklab, var(--nve-sys-scrollbar-thumb-color) 100%, currentColor 20%)
    var(--nve-sys-scrollbar-track-color);
  scrollbar-width: var(--nve-sys-scrollbar-width);
  height: var(--_height);
  background: var(--background);
  border: var(--border);
  border-bottom: var(--border-bottom);
  border-radius: var(--border-radius);
  width: var(--control-width);
  color: var(--color);
  align-items: center;
  display: flex;
  align-content: stretch;
  position: relative;
}

[name='caret'] {
  right: var(--nve-ref-size-300);
  align-self: center;
  pointer-events: none;
  position: absolute;
  transition: transform var(--nve-ref-animation-duration-200) cubic-bezier(0.4, 0, 0.2, 1);
}

::slotted(select) {
  appearance: none !important;
  color: var(--color) !important;
  padding: var(--padding) !important;
  font-size: var(--font-size) !important;
  cursor: var(--cursor) !important;
  font-weight: var(--font-weight) !important;
  text-align: var(--text-align) !important;
  height: var(--_height) !important;
  width: 100% !important;
  border: 0 !important;
  background: none !important;
  display: block !important;
}

[internal-host]:has(nve-menu:focus-within) [input] {
  outline: 0 !important;
}

:host(:state(selected)) {
  --font-weight: var(--nve-ref-font-weight-semibold);
}

:host([fit-content]:state(multiple)) [internal-host]:has(nve-tag) ::slotted(select) {
  width: var(--nve-ref-size-1000) !important;
}

:host(:state(multiple)) {
  --padding: 0 var(--nve-ref-size-300) 0 var(--nve-ref-size-100);
}

:host(:state(multiple)) [input] {
  padding: var(--padding);
}

:host(:state(multiple)) .tags {
  width: fit-content;
  display: flex;
  gap: var(--gap);
}

:host(:state(multiple)) .tags-label {
  padding: 0 var(--nve-ref-size-200);
  font-size: var(--font-size);
  cursor: var(--cursor);
  font-weight: var(--font-weight);
  position: absolute;
  left: 0;
  display: none;
  pointer-events: none;
}

:host(:state(multiple-overflow)) .tags,
:host([multiple-overflow]) .tags {
  opacity: 0;
  pointer-events: none;
  position: absolute;
}

:host(:state(multiple-overflow)) .tags-label,
:host([multiple-overflow]) .tags-label,
.placeholder.tags-label {
  display: block !important;
}

:host([size]:state(multiple)) .tags,
:host([size]:state(multiple)) .tags-label {
  display: none !important;
}

:host(:state(multiple)) ::slotted(select[multiple]) {
  opacity: 0 !important;
}

:host(:state(size)) {
  --_row-height: calc(var(--height) * var(--size) + var(--gap) * (var(--size) - 1));
  --_height: calc(var(--_row-height) + var(--nve-ref-border-width-md) * 2);
}

:host(:state(size)) [input] {
  overflow: auto;
  display: block;
}

:host(:state(size)) [input] slot:not([name]) {
  display: none;
}

:host([layout*='horizontal'][size]) ::slotted(label) {
  padding-block-start: var(--nve-ref-size-100) !important;
  align-self: start !important;
}

:host([layout*='horizontal'][size]) slot[name='label'] {
  align-self: start;
}

:host([status='error']),
:host(:state(invalid)),
:host(:state(error)) {
  --border: var(--nve-ref-border-width-md) solid var(--nve-sys-support-danger-emphasis-color);
}

:host([status='success']),
:host(:state(success)) {
  --border: var(--nve-ref-border-width-md) solid var(--nve-sys-support-success-emphasis-color);
}

:host(:state(disabled)) {
  --border: var(--nve-ref-border-width-md) solid transparent;
}

nve-tag {
  --background: color-mix(in oklab, var(--color) 15%, var(--_input-background));
  --padding: var(--nve-ref-size-100) var(--nve-ref-size-200);
  --cursor: pointer !important;
}

:host(:state(disabled)) nve-tag {
  pointer-events: none !important;
  --cursor: not-allowed !important;
}

nve-dropdown {
  --padding: var(--nve-ref-size-200);
}

nve-menu {
  --max-height: var(--scroll-height);
  --gap: var(--_gap);
}

nve-menu-item {
  --text-transform: initial;
  contain: content;
  content-visibility: auto;
}

nve-menu-item:not(:state(selected)) nve-icon[name='check'] {
  opacity: 0;
}

nve-menu-item nve-icon {
  --color: inherit;
}

slot:not([name]),
slot[name*='option-'] {
  display: contents;
}

:host([container='flat']) {
  --background: unset;
  --border-radius: 0;
  --box-shadow: none;
  --border-bottom: var(--nve-ref-border-width-md) solid var(--nve-ref-border-color-muted);
}

:host([container='flat']) [input] {
  background: inherit;
}

:host([container='flat']) nve-tag {
  --background: var(--nve-sys-interaction-field-background);
}

:host([container='flat']:focus-within) {
  --border-bottom: var(--nve-ref-border-width-md) solid var(--nve-ref-border-color-emphasis);
}

:host([container='flat'][size]) {
  --border-bottom: 0 !important;
}

:host([container='flat']:focus-within) [input] {
  outline: 0 !important;
}

:host([container='inline']) {
  --border-radius: 0;
  --box-shadow: none;
}

:host([container='inline']) [input] {
  background: inherit;
}
